import { useCallback, useEffect, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import DiscoverButtonCTA from '../../components/DiscoverButton/DiscoverButtonCTA'
import MobileAppPopup from '../../components/MobileAppPopup'
import UsernameUpdate from '../../components/username/UsernameUpdate'
import { useIsMobile } from '../../hooks/useIsMobile'
import { getPrerenderedExploreInspirations } from '../../queries/posts'
import { gotoLogIn, gotoSignUp } from '../../redux/actions/AuthActions'
import { getFollowList } from '../../redux/actions/FollowingActions'
import { loadSavedUserInspirationIds } from '../../redux/actions/SaveInspirationAction'
import { logEvent } from '../../utils/firebase'
import {
  CTAButton,
  CTAButtonBackground,
  CTAButtonContainer,
  ExploreFooter,
  ExploreHeader,
  ExploreSubtext,
  ExploreTitle,
  ExploreWrapper,
  FooterActions,
  FooterBackground,
  FooterContent,
  FooterCopyGroup,
  FooterCTAButton,
  FooterCTAButtonBackground,
  FooterCTAButtonContainer,
  FooterCTAText,
  FooterLoginText,
  FooterSubtext,
  FooterTitle,
  SectionTitle,
  SortDropdown,
  SortingControls,
  SortingSection,
  SortLabel
} from './Explore.styles'
import { ExploreInspirationsContainer } from './ExploreInspirationsContainer'

export const ExplorePage = ({ initialData }) => {
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const { isAuthenticated } = useSelector(state => state.auth)
  const following = useSelector(state => state.follows.following)

  const [sortBy, setSortBy] = useState('numLikes')
  const [userInspirations, setUserInspirations] = useState(initialData?.result?.userInspirations || [])
  const [hasReachedLimit, setHasReachedLimit] = useState(false)
  const [showSignupWall, setShowSignupWall] = useState(false)
  const [loading, setLoading] = useState('idle')
  const [page, setPage] = useState(1)
  const [end, setEnd] = useState(false)

  useEffect(() => {
    batch(() => {
      dispatch(loadSavedUserInspirationIds())
      if (isAuthenticated) {
        dispatch(getFollowList())
      }
    })
  }, [dispatch, isAuthenticated])

  // Check if non-authenticated user has scrolled through ~60 designs
  useEffect(() => {
    if (!isAuthenticated && userInspirations?.length >= 60) {
      setHasReachedLimit(true)
      setShowSignupWall(true)
    }
  }, [isAuthenticated, userInspirations?.length])

  const handleSortChange = useCallback(
    async event => {
      if (!isAuthenticated) {
        dispatch(gotoSignUp())
        return
      }

      const newSortBy = event.target.value
      setSortBy(newSortBy)
      setLoading('pending')
      setHasReachedLimit(false)
      setShowSignupWall(false)
      setPage(1)
      setEnd(false)

      try {
        // Fetch new data with the selected sort
        let newData = []

        if (newSortBy === 'following') {
          newData = await getPrerenderedExploreInspirations('', 20, 0, following)
        } else {
          newData = await getPrerenderedExploreInspirations(newSortBy, 20, 0)
        }

        setUserInspirations(newData?.result?.userInspirations || [])
      } catch (error) {
        console.error('Failed to fetch explore data:', error)
        setUserInspirations([])
      } finally {
        setLoading('idle')
      }
    },
    [isAuthenticated, following]
  )

  const handleLoadMore = useCallback(async () => {
    if (!isAuthenticated && hasReachedLimit) {
      setShowSignupWall(true)
      return
    }

    if (loading === 'pending' || end) {
      return
    }

    setLoading('pending')

    try {
      // Load more data for the current sort
      const moreData = await getPrerenderedExploreInspirations(sortBy, 20, page + 1)
      const newInspirations = moreData?.result?.userInspirations || []

      if (newInspirations.length === 0) {
        setEnd(true)
      } else {
        setUserInspirations(prev => [...prev, ...newInspirations])
        setPage(prev => prev + 1)
      }
    } catch (error) {
      console.error('Failed to load more explore data:', error)
      setEnd(true)
    } finally {
      setLoading('idle')
    }
  }, [loading, end, sortBy, page, isAuthenticated, hasReachedLimit])

  const handleSignUp = useCallback(() => {
    logEvent('explore_signup_clicked')
    dispatch(gotoSignUp())
  }, [dispatch])

  const handleLogIn = useCallback(() => {
    logEvent('explore_login_clicked')
    dispatch(gotoLogIn())
  }, [dispatch])

  return (
    <>
      <ExploreWrapper>
        <ExploreHeader>
          <ExploreTitle>Interior Design Ideas & Home Decor Inspiration by Real Users</ExploreTitle>
          <ExploreSubtext>
            Browse thousands of real room designs and home decor ideas created by everyday users in the DecorMatters app. This visual gallery is your creative hub for discovering decorating inspiration by room, style, color, and mood. Whether you're
            into cozy bedrooms or minimalist kitchens, you'll find fresh room ideas to spark your next home project.
          </ExploreSubtext>

          <CTAButtonContainer>
            <CTAButtonBackground />
            <CTAButton onClick={handleSignUp}>Start Creating with DecorMatters</CTAButton>
          </CTAButtonContainer>

          <SortingSection>
            <SectionTitle>Explore Popular Room Design Ideas</SectionTitle>
            <SortingControls>
              <SortLabel>Sort By</SortLabel>
              <SortDropdown value={sortBy} onChange={handleSortChange}>
                <option value="numLikes">Popular</option>
                <option value="">Newest</option>
                {isAuthenticated && <option value="following">Following</option>}
              </SortDropdown>
            </SortingControls>
          </SortingSection>
        </ExploreHeader>

        <ExploreInspirationsContainer
          userInspirations={userInspirations}
          onFetchMore={handleLoadMore}
          loading={loading}
          end={end}
          header={null}
          footer={null}
          paddingStart={isAuthenticated ? (isMobile ? 20 : 80) : isMobile ? 20 : 250}
          paddingBottom={isAuthenticated ? 115 : 62}
          shouldShowLoginProposal={showSignupWall}
        />

        {!isAuthenticated && (
          <ExploreFooter>
            <FooterBackground />
            <FooterContent>
              <FooterCopyGroup>
                <FooterTitle>Explore Real Home Decor Ideas by Room, Style, & Mood</FooterTitle>
                <FooterSubtext>
                  Looking for fresh home decor ideas or room design inspiration? Discover thousands of real designs created in the DecorMatters app by a creative community of interior design lovers. From decorating a small apartment to refreshing a
                  living room, our Explore page helps you find the right styles, colors, and moods for any space.
                </FooterSubtext>
                <FooterCTAText>Create a free account to explore more creations.</FooterCTAText>
              </FooterCopyGroup>

              <FooterActions>
                <FooterCTAButtonContainer>
                  <FooterCTAButtonBackground />
                  <FooterCTAButton onClick={handleSignUp}>Free Sign Up</FooterCTAButton>
                </FooterCTAButtonContainer>
                <FooterLoginText>
                  <span className="login-prompt">Already registered? </span>
                  <button className="login-link" onClick={handleLogIn}>
                    Log In
                  </button>
                </FooterLoginText>
              </FooterActions>
            </FooterContent>
          </ExploreFooter>
        )}

        <UsernameUpdate />
      </ExploreWrapper>
      <MobileAppPopup />
      <DiscoverButtonCTA />
    </>
  )
}
